/**
 * StatsPills — Tank Redesign Overview stat tiles + bottom ingest strip.
 *
 * Source: design_handoff_tank/tank-redesign/screens-base.jsx §
 * Screen02Overview. Seven tiles in a row (Total + the six canonical
 * statuses) on top; an ingest counter on the right side of the same row.
 *
 * Each status tile is a filter toggle. Clicking it scopes the grid to
 * that status; clicking again clears. Multi-active filtering is
 * allowed — the URL hash reflects the combination (handled upstream).
 *
 * The "Total" tile clears all filters. Active tiles use the redesign's
 * status palette for border + count; inactive tiles read on the
 * default panel surface.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import IngestionFailureList from '../../shared/components/IngestionFailureList';
import { Stat } from '../../shared/components/redesign';
import styles from './StatsPills.module.css';

const STATUS_TILES = [
  { key: 'total',      label: 'Total',      kind: 'pending'   },
  { key: 'done',       label: 'Done',       kind: 'done'      },
  { key: 'review',     label: 'Review',     kind: 'exported'  },
  { key: 'tagging',    label: 'Tagging',    kind: 'tagging'   },
  { key: 'attention',  label: 'Attention',  kind: 'attention' },
  { key: 'skipped',    label: 'Skipped',    kind: 'skipped'   },
  { key: 'processing', label: 'Processing', kind: 'pending'   },
];

/**
 * Sum the photo counts for the given filter key. The legacy `totals`
 * dict has finer-grained sub-states (tagging / review / etc.) that
 * collapse onto the redesign's six canonical kinds — see
 * `getRedesignStatusKind` for the full mapping rationale.
 */
function getCountForKey(key, totals) {
  if (!totals) return 0;
  switch (key) {
    case 'total':
      return totals.total || 0;
    case 'done':
      // Done = approved + already exported.
      return (totals.done || 0) + (totals.exported || 0);
    case 'review':
      // Pending with all faces labeled, ready for approval.
      return totals.review || 0;
    case 'tagging':
      // Pending with at least one unlabeled face.
      return totals.tagging || 0;
    case 'attention':
      // Operator-relevant umbrella: backend `attention` (e.g. no faces
      // detected) plus real failures (`error` / `export_failed`). The
      // tile stays amber so routine missing-face cases don't shout;
      // individual error photos render red on their card pill.
      return (
        (totals.attention || 0)
        + (totals.error || 0)
        + (totals.export_failed || 0)
      );
    case 'skipped':
      return totals.skipped || 0;
    case 'processing':
      // Pre-review pipeline + transient states.
      return (
        (totals.processing || 0)
        + (totals.ingested || 0)
        + (totals.recropping || 0)
        + (totals.sent_to_editor || 0)
      );
    default:
      return 0;
  }
}

/**
 * Total count of all photos for the percentage calculation.
 */
function getDenominator(totals) {
  return totals?.total || 0;
}

function getIngestStateColor(ingestionStats) {
  if (!ingestionStats) return 'ok';
  const { received, ingested, failed, duplicate } = ingestionStats;
  if (received > 0 && failed > 0 && failed / received > 0.1) return 'error';
  if ((failed || 0) > 0 || (duplicate || 0) > 0) return 'warn';
  if (received === ingested) return 'ok';
  return 'ok';
}

export default function StatsPills({
  totals,
  activeFilters,
  onToggle,
  onClear,
  metrics: _metrics,
  ingestionStats,
}) {
  void _metrics; // legacy prop; not consumed in redesign tile row

  const [ingestExpanded, setIngestExpanded] = useState(false);
  const [showFailureList, setShowFailureList] = useState(false);
  const collapseTimerRef = useRef(null);

  useEffect(() => {
    if (!ingestExpanded) return undefined;
    collapseTimerRef.current = setTimeout(() => setIngestExpanded(false), 5000);
    return () => clearTimeout(collapseTimerRef.current);
  }, [ingestExpanded]);

  const toggleIngestDetail = useCallback(() => setIngestExpanded((v) => !v), []);

  void getDenominator; // %-of-total tile delta is parked; see Overview design.

  const handleTileClick = (key) => {
    if (key === 'total') {
      onClear?.();
      return;
    }
    onToggle?.(key);
  };

  const handleTileKey = (key) => (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleTileClick(key);
  };

  const isTileActive = (key) => {
    if (key === 'total') return !activeFilters || activeFilters.length === 0;
    return activeFilters?.includes(key);
  };

  const ingestState = getIngestStateColor(ingestionStats);
  const hasIngestionData = ingestionStats && typeof ingestionStats.received === 'number';
  const ingestReceived = hasIngestionData ? ingestionStats.received : null;
  const ingestIngested = hasIngestionData ? ingestionStats.ingested : null;
  const ingestFailed = hasIngestionData ? (ingestionStats.failed || 0) : 0;
  const ingestDuplicate = hasIngestionData ? (ingestionStats.duplicate || 0) : 0;

  return (
    <>
      <div className={styles.row}>
        {STATUS_TILES.map((tile) => {
          const active = isTileActive(tile.key);
          const count = getCountForKey(tile.key, totals);
          // Pulse the Processing tile while photos are mid-pipeline so
          // editors get a visible "files are coming in" cue.
          const pulsing = tile.key === 'processing' && count > 0;
          return (
            <div
              key={tile.key}
              role="button"
              tabIndex={0}
              className={`${styles.tileWrap} ${pulsing ? styles.tilePulsing : ''}`}
              onClick={() => handleTileClick(tile.key)}
              onKeyDown={handleTileKey(tile.key)}
              aria-pressed={active}
              aria-label={`${tile.label}: ${count}${pulsing ? ' (incoming)' : ''}`}
              title={pulsing ? `${tile.label} — files incoming` : tile.label}
            >
              <Stat
                label={tile.label}
                value={count}
                kind={tile.kind}
                active={active}
                className={active ? styles.tileActive : styles.tileIdle}
              />
            </div>
          );
        })}

        <div className={styles.spacer} />

        <button
          type="button"
          className={`${styles.ingest} ${styles[`ingest-${ingestState}`]}`}
          onClick={toggleIngestDetail}
          aria-expanded={ingestExpanded}
        >
          <span className={styles.ingestLabel}>Ingest</span>
          <span className={styles.ingestValue}>
            {hasIngestionData ? `${ingestIngested} / ${ingestReceived}` : (totals?.ingested || 0)}
          </span>
        </button>
      </div>

      {ingestExpanded && hasIngestionData && (
        <div className={styles.ingestDetail}>
          <span>Received: <strong>{ingestReceived}</strong></span>
          <span>Ingested: <strong className={styles.ingestOk}>{ingestIngested}</strong></span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              if (ingestFailed > 0) setShowFailureList((v) => !v);
            }}
            className={ingestFailed > 0 ? styles.ingestFailedClickable : undefined}
          >
            Failed: <strong className={ingestFailed > 0 ? styles.ingestError : ''}>{ingestFailed}</strong>
          </span>
          <span>
            Duplicates:{' '}
            <strong className={ingestDuplicate > 0 ? styles.ingestWarn : ''}>{ingestDuplicate}</strong>
          </span>
        </div>
      )}

      {showFailureList && (
        <IngestionFailureList onClose={() => setShowFailureList(false)} />
      )}
    </>
  );
}
